import { Link, useParams } from "react-router-dom";
import { CheckinTags } from "../components/CheckinTags";
import { useSessionDetail, SessionExerciseRow } from "../lib/queries";
import { LoadingState, ErrorState } from "../components/Loading";
import { PageShell } from "../components/PageShell";
import {
  PlanElement,
  PlanGroup,
  parsePlanBody,
  describePrescription,
  describeCircuit,
  measureOf,
  formatSeconds,
  groupOf,
} from "../lib/planElements";

/**
 * One training session, in full: what was prescribed next to what was actually done.
 *
 * The history list could only ever show what happened -- "8 reps", with nothing to compare it to. Whether
 * that was the whole prescription or half of it was invisible, and that comparison is most of why a
 * session gets read at all. Here every exercise carries its prescription, and its sets sit underneath in
 * order, so a set that fell apart is visible without arithmetic.
 *
 * Reachable by coach and family alike (the server gates it on the same rule as the history it belongs
 * to) -- an athlete looking back at their own week sees exactly what their coach sees.
 */
export function SessionDetail({ audience = "coach" }: { audience?: "coach" | "family" }) {
  const { sessionId } = useParams<{ sessionId: string }>();
  const query = useSessionDetail(sessionId);

  if (query.isPending) {
    return (
      <PageShell title="Session">
        <LoadingState label="Loading session…" />
      </PageShell>
    );
  }

  if (query.isError) {
    return (
      <PageShell title="Session">
        <ErrorState error={query.error} onRetry={() => query.refetch()} />
      </PageShell>
    );
  }

  const { session, exercises, client, plan } = query.data;
  const planBody = parsePlanBody(plan.elements);
  const elements = planBody.elements;
  // A family viewer has no /coach route to go back to, and the athlete's own home is where they came
  // from -- the same page under two audiences rather than two near-identical pages.
  const backTo = audience === "coach" ? `/coach/clients/${client.id}` : "/family";
  const backLabel = audience === "coach" ? `Back to ${client.name}` : "Back to training";

  // Sets grouped by the exercise they belong to. The stored row carries an element *index*, not a name,
  // so an exercise renamed later cannot rewrite history.
  const byExercise = new Map<number, SessionExerciseRow[]>();
  for (const exercise of exercises) {
    byExercise.set(exercise.element_index, [...(byExercise.get(exercise.element_index) ?? []), exercise]);
  }

  return (
    <PageShell
      title={formatCompletedAt(session.completed_at)}
      actions={
        <Link className="link-button" to={backTo}>
          {backLabel}
        </Link>
      }
    >
      <div className="client-card">
        {plan.title && <span className="client-name">{plan.title}</span>}
        {session.issue_flagged ? (
          <span className="badge badge-danger">Issue flagged</span>
        ) : (
          <span className="badge badge-ok">Completed</span>
        )}
        <span className="badge">
          {session.logged_by_role === "guardian" ? "Logged by guardian" : "Logged by athlete"}
        </span>
        {session.session_rpe !== null && <span className="badge">Session RPE {session.session_rpe}/10</span>}
      </div>
      <CheckinTags checkin={session.checkin} />

      {/* Said out loud rather than quietly shown: without the snapshot the prescription is whatever the
          plan says today, which may not be what was asked for on the day. */}
      {!plan.from_snapshot && (
        <p className="form-hint">
          This session predates prescriptions being saved with the session, so what&rsquo;s shown below is
          the plan as it stands now. It may have been edited since.
        </p>
      )}

      {byExercise.size === 0 ? (
        <p className="empty-state">No sets were recorded for this session.</p>
      ) : (
        [...byExercise.entries()]
          .sort(([a], [b]) => a - b)
          .map(([elementIndex, sets]) => (
            <ExerciseBlock
              key={elementIndex}
              index={elementIndex}
              element={elements[elementIndex]}
              group={groupOf(planBody, elements[elementIndex])}
              sets={[...sets].sort((a, b) => a.set_index - b.set_index)}
            />
          ))
      )}
    </PageShell>
  );
}

/** One prescribed exercise and every set logged against it. */
function ExerciseBlock({
  index,
  element,
  group,
  sets,
}: {
  index: number;
  element: PlanElement | undefined;
  /** The circuit it was part of, which is what turns its "sets" into rounds. */
  group: PlanGroup | null;
  sets: SessionExerciseRow[];
}) {
  const prescription = element ? describePrescription(element, Boolean(group)) : null;

  return (
    <section className="session-exercise-block">
      <h2>
        {index + 1}. {element?.name ?? `Exercise ${index + 1}`}
      </h2>
      {/* Named here as well as on the plan, because a coach reading "set 3" of an exercise needs to
          know whether that was a third set or a third time round the circuit. */}
      {group && <p className="form-label">{`${group.name || "Circuit"} — ${describeCircuit(group)}`}</p>}
      <p className="form-label">Prescribed</p>
      <p className="element-prescription">{prescription ?? "No prescription recorded"}</p>
      {element?.notes && <p className="element-notes">{element.notes}</p>}

      <p className="form-label">Actually done</p>
      <ol className="set-list">
        {sets.map((set) => (
          <li key={set.id} className={set.status === "skipped" ? "set-row set-skipped" : "set-row"}>
            <span className="element-tag">{`${group ? "Round" : "Set"} ${set.set_index + 1}`}</span>
            <span className="set-actual">{describeActual(set)}</span>
            <span className="set-target">{targetFor(element)}</span>
            {set.flagged && <span className="badge badge-danger">Flagged</span>}
            {set.flag_note && <span className="element-notes">{set.flag_note}</span>}
          </li>
        ))}
      </ol>
    </section>
  );
}

/** "8 reps · 35 lb", "30s", or "skipped". Never an empty string -- a blank cell reads as a bug. */
function describeActual(set: SessionExerciseRow): string {
  if (set.status === "skipped") return "Skipped";
  const parts = [
    set.reps !== null ? `${set.reps} reps` : null,
    set.duration_seconds !== null ? formatSeconds(set.duration_seconds) : null,
    describeWeight(set),
    set.rpe !== null ? `RPE ${set.rpe}` : null,
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(" · ") : "Completed, nothing recorded";
}

/**
 * Bodyweight is a real answer rather than a missing number, so it is spelled out. The unit comes off the
 * set itself, never off the account -- changing a preference must not reinterpret history.
 */
function describeWeight(set: SessionExerciseRow): string | null {
  if (set.weight_unit === "bodyweight") return "bodyweight";
  if (set.weight === null) return null;
  return set.weight_unit ? `${set.weight} ${set.weight_unit}` : `${set.weight}`;
}

/** What this set was aiming at, so the comparison is on the same line rather than a scroll away. */
function targetFor(element: PlanElement | undefined): string {
  if (!element) return "";
  if (measureOf(element) === "time") {
    return element.durationSeconds ? `target ${formatSeconds(element.durationSeconds)}` : "";
  }
  return element.reps ? `target ${element.reps} reps` : "";
}

/**
 * The viewer's own locale and timezone. `completed_at` is a UTC instant from the Lambda clock, so a
 * session finished late in the evening would otherwise display as the next day west of UTC.
 */
function formatCompletedAt(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" });
}
